import { generateUuid } from "./uuid";
import cookies from "../constants/cookies";
import Cookies from "js-cookie";
import urls from "../constants/urls";

import {
  SearchTermPredictionBuilder,
  SearchCollectionBuilder,
  ProductSearchBuilder,
  ProductCategorySearchBuilder,
  Searcher,
} from "@relewise/client";

// Testing
// window.Cookiebot.consent.marketing = false;
// const STAGING = true;

const STAGING = window.location.host === "localhost:8000";

const constants = {
  RELEWISE_STAGE_KEY: "z9bQzNv3mG1*7h",
  RELEWISE_PROD_KEY: "CU^Xd71l^3xPn8",
};

const assortments = {
  com: 223,
  de: 137,
  dk: 143,
  se: 152,
  no: 143,
  uk: 160,
};

const endpoints = {
  ProductView: "TrackProductViewRequest",
  Cart: "TrackCartRequest",
  ProductsViewedAfterViewingProductRequest: "ProductsViewedAfterViewingProductRequest",
  PowerStep: "PurchasedWithProductRequest",
  CompleteTheLook: "PurchasedWithProductRequest",
  PopularProducts: "PopularProductsRequest",
  PopularProductsPersonal: "PersonalProductRecommendationRequest",
  FallbackPopularProducts: "PopularProductsRequest",
};

const requestDefaults = {
  selectedProductProperties: {
    DisplayName: true,
    Pricing: true,
  },
  selectedVariantProperties: {
    DisplayName: true,
    Pricing: true,
    DataKeys: ["COM_ShopifyHandle", "DE_ShopifyHandle", "DK_ShopifyHandle", "SE_ShopifyHandle", "UK_ShopifyHandle", "NO_ShopifyHandle", "ImageUrls"],
  },
};

let temporaryId = "";
let authenticatedId = window.mosmosh.user;

export function relewiseTrack(trigger, options = {}) {
  let payload = {};
  let object = {};

  payload[trigger] = {
    User: getUserObject(),
    Typename: trigger,
  };

  if (trigger === "ProductView") {
    object = getTrackProductViewRequestObject(options);
  } else if (trigger === "Cart") {
    const filtratedLineItems = theme.cart.items.filter((item) => item.sku.substring(0, 6) >= 6);

    let total_price = 0;
    object = new Promise((resolve, reject) => {
      if (theme.cart.items.length) {
        resolve({
          Name: "default",
          LineItems: filtratedLineItems.map((item) => {
            total_price += item.line_price;

            return {
              Product: {
                Id: item.sku.substring(0, 6),
              },
              Variant: {
                Id: item.sku,
              },
              Quantity: item.quantity,
              LineTotal: parseFloat(item.line_price / 100),
            };
          }),
          Subtotal: {
            Amount: parseFloat(total_price / 100),
            Currency: {
              value: theme.cart.currency,
            },
          },
        });
      } else {
        reject("Product ID is too short");
      }
    });
  }

  object
    .then((response) => {
      Object.assign(payload[trigger], response);
      postDataObject(endpoints[trigger], payload);
    })
    .catch((error) => {
      console.log(error);
    });
}

export function relewiseRecommend(trigger, options = {}) {
  let payload = {};

  if (trigger === "ProductsViewedAfterViewingProductRequest") {
    payload = getProductsViewedAfterViewingProductObject(options);
  }

  if (trigger === "CompleteTheLook") {
    payload = getCompleteTheLookObject(options);
  }

  if (trigger === "PopularProducts") {
    payload = getPopularProducts(options);
  }

  if (trigger === "PopularProductsPersonal") {
    payload = getPopularProductsPersonal(options);
  }

  if (trigger === "PowerStep") {
    payload = getPowerStepObject(options);
  }

  if (trigger === "FallbackPopularProducts") {
    payload = getFallbackPopularProducts(options);
  }

  if (trigger === "CartRecommendations") {
    payload = getCartRecommendationsObject(options);
  }

  return postDataObject(endpoints[trigger], payload);
}

export function getUserObject() {
  const marketingConsent = window.CookieScript?.instance.currentState().categories.indexOf("targeting") !== -1;

  const urlParams = new URLSearchParams(window.location.search);
  // const KlaviyoId = urlParams.get("klaviyo_id");
  const voyadoId = urlParams.get("voyado_user_id") || sessionStorage.getItem("voyado_user_id");

  if (urlParams.has("voyado_user_id") && urlParams.get("voyado_user_id").length) {
    sessionStorage.setItem("voyado_user_id", urlParams.get("voyado_user_id"));
  }

  if (marketingConsent) {
    temporaryId = Cookies.get(cookies.RELEWISE_TEMPORARY_ID);

    if (!temporaryId) {
      temporaryId = generateUuid();
      Cookies.set(cookies.RELEWISE_TEMPORARY_ID, temporaryId, {
        expires: 365,
      });
    }
  }

  return {
    ...(temporaryId && { TemporaryId: temporaryId }),
    ...(authenticatedId && { AuthenticatedId: authenticatedId }),
    ...(voyadoId && voyadoId.length && { Identifiers: { VoyadoId: voyadoId } }),
    classifications: { Country: Cookies.get(cookies.SELECTED_LOCATION) },
  };
}

function getTrackProductViewRequestObject(options) {
  //console.log("RE / getTrackProductViewRequestObject: ", options.crossStoreProductId.length);

  return new Promise((resolve, reject) => {
    if (options.crossStoreProductId.length >= 6) {
      resolve({
        Product: {
          Id: options.crossStoreProductId,
        },
        Variant: {
          Id: options.variantId,
        },
      });
    } else {
      reject("Product ID is too short");
    }
  });
}

function getProductsViewedAfterViewingProductObject(options) {
  let filter1 = {
    $type: "Relewise.Client.Requests.Filters.ProductCategoryIdFilter, Relewise.Client",
    CategoryIds: options.collections,
    EvaluationScope: "Ancestor",
    TypeName: "ProductCategoryIdFilter",
  };

  let filter2 = {
    $type: "Relewise.Client.Requests.Filters.ProductDataFilter, Relewise.Client",
    Key: "Vendor",
    FilterOutIfKeyIsNotFound: true,
    MustMatchAllConditions: true,
    Conditions: {
      Items: [
        {
          $type: "Relewise.Client.Requests.Conditions.EqualsCondition, Relewise.Client",
          Value: {
            Value: options.vendor,
          },
          TypeName: "EqualsCondition",
        },
      ],
    },
    TypeName: "ProductDataFilter",
  };

  if (options.c2s) {
    filter1 = {
      $type: "Relewise.Client.Requests.Filters.ProductDataFilter, Relewise.Client",
      Key: "Vendor",
      FilterOutIfKeyIsNotFound: true,
      MustMatchAllConditions: true,
      Conditions: {
        Items: [
          {
            $type: "Relewise.Client.Requests.Conditions.EqualsCondition, Relewise.Client",
            Value: {
              Value: "Mos Mosh", // All DK C2S products are having this Vendor
            },
            TypeName: "EqualsCondition",
          },
        ],
      },
      TypeName: "ProductDataFilter",
    };

    filter2 = {
      $type: "Relewise.Client.Requests.Filters.ProductDataFilter, Relewise.Client",
      Key: "Reloved",
      FilterOutIfKeyIsNotFound: true,
      MustMatchAllConditions: true,
      Conditions: {
        Items: [
          {
            $type: "Relewise.Client.Requests.Conditions.EqualsCondition, Relewise.Client",
            Value: {
              Type: "Boolean",
              Value: true,
            },
            TypeName: "EqualsCondition",
          },
        ],
      },
      TypeName: "ProductDataFilter",
    };
  }

  let payload = {
    ProductAndVariantId: {
      ProductId: options.crossStoreProductId,
      VariantId: options.variantId,
    },
    Settings: {
      NumberOfRecommendations: 10,
      AllowFillIfNecessaryToReachNumberOfRecommendations: true,
      AllowReplacingOfRecentlyShownRecommendations: true,
      RecommendVariant: true,
      SelectedProductProperties: {
        DisplayName: true,
        Pricing: true,
      },
      SelectedVariantProperties: {
        DisplayName: true,
        Pricing: true,
        DataKeys: ["COM_ShopifyHandle", "DE_ShopifyHandle", "DK_ShopifyHandle", "SE_ShopifyHandle", "UK_ShopifyHandle", "NO_ShopifyHandle", "ImageUrls"],
      },
    },
    Filters: {
      Items: [
        filter1,
        {
          $type: "Relewise.Client.Requests.Filters.ProductAssortmentFilter, Relewise.Client",
          Assortments: [assortments[window.mosmosh.shop_code]],
          TypeName: "ProductAssortmentFilter",
        },
        {
          $type: "Relewise.Client.Requests.Filters.VariantAssortmentFilter, Relewise.Client",
          Assortments: [assortments[window.mosmosh.shop_code]],
          TypeName: "VariantAssortmentFilter",
        },
        filter2,
      ],
    },
    Language: {
      Value: window.mosmosh.shop_local.iso,
    },
    User: getUserObject(),
    DisplayedAtLocationType: options.c2s ? "Product Detail page - ReLoved" : "Product detail page",
    Currency: {
      Value: window.mosmosh.currency.iso,
    },
  };

  return payload;
}

function getCompleteTheLookObject(options) {
  let payload = {
    ProductAndVariantId: {
      ProductId: options.crossStoreProductId,
      VariantId: options.variantId,
    },
    Settings: {
      NumberOfRecommendations: 10,
      AllowFillIfNecessaryToReachNumberOfRecommendations: true,
      AllowReplacingOfRecentlyShownRecommendations: false,
      RecommendVariant: true,
      SelectedProductProperties: requestDefaults.selectedProductProperties,
      SelectedVariantProperties: requestDefaults.selectedVariantProperties,
    },
    Language: {
      Value: window.mosmosh.shop_local.iso,
    },
    User: getUserObject(),
    Filters: {
      Items: [
        {
          $type: "Relewise.Client.Requests.Filters.ProductDataFilter, Relewise.Client",
          Key: "Vendor",
          FilterOutIfKeyIsNotFound: true,
          MustMatchAllConditions: true,
          Conditions: {
            Items: [
              {
                $type: "Relewise.Client.Requests.Conditions.EqualsCondition, Relewise.Client",
                Value: {
                  Value: options.vendor,
                },
                TypeName: "EqualsCondition",
              },
            ],
          },
          TypeName: "ProductDataFilter",
        },
        {
          $type: "Relewise.Client.Requests.Filters.ProductAssortmentFilter, Relewise.Client",
          Assortments: [assortments[window.mosmosh.shop_code]],
          TypeName: "ProductAssortmentFilter",
        },
        {
          $type: "Relewise.Client.Requests.Filters.VariantAssortmentFilter, Relewise.Client",
          Assortments: [assortments[window.mosmosh.shop_code]],
          TypeName: "VariantAssortmentFilter",
        },
        {
          $type: "Relewise.Client.Requests.Filters.ProductIdFilter, Relewise.Client",
          ProductIds: options.variantNames.map((variantName) => variantName.substring(0, 6)),
          TypeName: "ProductIdFilter",
        },
        {
          $type: "Relewise.Client.Requests.Filters.VariantDataFilter, Relewise.Client",
          Key: "ShopTheLook",
          FilterOutIfKeyIsNotFound: true,
          MustMatchAllConditions: true,
          Conditions: {
            Items: [
              {
                $type: "Relewise.Client.Requests.Conditions.ContainsCondition, Relewise.Client",
                Value: {
                  Type: "StringList",
                  Value: {
                    $type:
                      "System.Collections.Generic.List`1[[System.String, System.Private.CoreLib]], System.Private.CoreLib",
                    $values: options.variantNames,
                  },
                },
                ValueCollectionEvaluationMode: "Any",
                TypeName: "ContainsCondition",
              },
            ],
          },
          TypeName: "VariantDataFilter",
        },
      ],
    },
    DisplayedAtLocationType: "Product detail page - Complete the look",
  };

  return payload;
}

function getPowerStepObject(options) {
  let payload = {
    ProductAndVariantId: {
      ProductId: options.crossStoreProductId,
      VariantId: options.variantId,
    },
    Settings: {
      NumberOfRecommendations: 6,
      AllowFillIfNecessaryToReachNumberOfRecommendations: false,
      AllowReplacingOfRecentlyShownRecommendations: false,
      RecommendVariant: true,
      SelectedProductProperties: requestDefaults.selectedProductProperties,
      SelectedVariantProperties: requestDefaults.selectedVariantProperties,
    },
    Language: {
      Value: window.mosmosh.shop_local.iso,
    },
    User: getUserObject(),
    Filters: {
      Items: [
        {
          $type: "Relewise.Client.Requests.Filters.ProductDataFilter, Relewise.Client",
          Key: "Vendor",
          FilterOutIfKeyIsNotFound: true,
          MustMatchAllConditions: true,
          Conditions: {
            Items: [
              {
                $type: "Relewise.Client.Requests.Conditions.EqualsCondition, Relewise.Client",
                Value: {
                  Value: options.vendor,
                },
                TypeName: "EqualsCondition",
              },
            ],
          },
          TypeName: "ProductDataFilter",
        },
        {
          $type: "Relewise.Client.Requests.Filters.ProductAssortmentFilter, Relewise.Client",
          Assortments: [assortments[window.mosmosh.shop_code]],
          TypeName: "ProductAssortmentFilter",
        },
        {
          $type: "Relewise.Client.Requests.Filters.VariantAssortmentFilter, Relewise.Client",
          Assortments: [assortments[window.mosmosh.shop_code]],
          TypeName: "VariantAssortmentFilter",
        },
      ],
    },
    DisplayedAtLocationType: "Product detail page - Power step",
  };

  return payload;
}

function getPopularProducts(options) {
  let payload = {
    basedOn: options.basedOn, // MostPurchased  or MostViewed
    sinceMinutesAgo: options.minutesAgo, // convert daysAgo to Minutes
    Settings: {
      NumberOfRecommendations: 10,
      AllowFillIfNecessaryToReachNumberOfRecommendations: true,
      AllowReplacingOfRecentlyShownRecommendations: true,
      RecommendVariant: true,
      SelectedProductProperties: {
        DisplayName: true,
        Pricing: true,
      },
    },
    Language: {
      Value: window.mosmosh.shop_local.iso,
    },
    User: getUserObject(),
    Currency: {
      Value: window.mosmosh.currency.iso,
    },
    Filters: {
      Items: [
        {
          $type: "Relewise.Client.Requests.Filters.ProductDataFilter, Relewise.Client",
          Key: "Vendor",
          FilterOutIfKeyIsNotFound: true,
          MustMatchAllConditions: true,
          Conditions: {
            Items: [
              {
                $type: "Relewise.Client.Requests.Conditions.EqualsCondition, Relewise.Client",
                Value: {
                  Value: options.vendor,
                },
                TypeName: "EqualsCondition",
              },
            ],
          },
          TypeName: "ProductDataFilter",
        },
        {
          $type: "Relewise.Client.Requests.Filters.ProductAssortmentFilter, Relewise.Client",
          Assortments: [assortments[window.mosmosh.shop_code]],
          TypeName: "ProductAssortmentFilter",
        },
        {
          $type: "Relewise.Client.Requests.Filters.VariantAssortmentFilter, Relewise.Client",
          Assortments: [assortments[window.mosmosh.shop_code]],
          TypeName: "VariantAssortmentFilter",
        },
      ],
    },
  };
  return payload;
}

function getPopularProductsPersonal(options) {
  let payload = {
    sinceMinutesAgo: options.minutesAgo, // TODO: convert daysAgo to Minutes
    Settings: {
      NumberOfRecommendations: 10,
      AllowFillIfNecessaryToReachNumberOfRecommendations: true,
      AllowReplacingOfRecentlyShownRecommendations: true,
      RecommendVariant: true,
      SelectedProductProperties: {
        DisplayName: true,
        Pricing: true,
      },
    },
    Language: {
      Value: window.mosmosh.shop_local.iso,
    },
    User: getUserObject(),
    Currency: {
      Value: window.mosmosh.currency.iso,
    },
    Filters: {
      Items: [
        {
          $type: "Relewise.Client.Requests.Filters.ProductDataFilter, Relewise.Client",
          Key: "Vendor",
          FilterOutIfKeyIsNotFound: true,
          MustMatchAllConditions: true,
          Conditions: {
            Items: [
              {
                $type: "Relewise.Client.Requests.Conditions.EqualsCondition, Relewise.Client",
                Value: {
                  Value: options.vendor,
                },
                TypeName: "EqualsCondition",
              },
            ],
          },
          TypeName: "ProductDataFilter",
        },
        {
          $type: "Relewise.Client.Requests.Filters.ProductAssortmentFilter, Relewise.Client",
          Assortments: [assortments[window.mosmosh.shop_code]],
          TypeName: "ProductAssortmentFilter",
        },
        {
          $type: "Relewise.Client.Requests.Filters.VariantAssortmentFilter, Relewise.Client",
          Assortments: [assortments[window.mosmosh.shop_code]],
          TypeName: "VariantAssortmentFilter",
        },
      ],
    },
  };
  return payload;
}

function getFallbackPopularProducts(options) {
  let payload = {
    BasedOn: "MostViewed",
    SinceMinutesAgo: 7200,
    Settings: {
      NumberOfRecommendations: 10,
      AllowFillIfNecessaryToReachNumberOfRecommendations: false,
      AllowReplacingOfRecentlyShownRecommendations: true,
      RecommendVariant: true,
      SelectedProductProperties: {
        DisplayName: true,
        CategoryPaths: false,
        Assortments: false,
        Pricing: true,
        AllData: false,
        ViewedByUserInfo: false,
        PurchasedByUserInfo: false,
        Brand: false,
        AllVariants: false,
        DataKeys: ["Vendor", "InStock", "Tags", "HasAnyModelImages", "HasNoImages"],
      },
      SelectedVariantProperties: {
        DisplayName: true,
        Pricing: true,
        AllSpecifications: false,
        Assortments: false,
        AllData: false,
        DataKeys: ["ImageUrls", "InventoryQuantity", "HasAnyModelImages", "HasNoImages"],
      },
      PrioritizeDiversityBetweenRequests: false,
    },
    Language: {
      Value: window.mosmosh.shop_local.iso,
    },
    User: getUserObject(),
    Currency: {
      Value: window.mosmosh.currency.iso,
    },
    RelevanceModifiers: {},
    Filters: {
      Items: [
        {
          $type: "Relewise.Client.Requests.Filters.ProductCategoryIdFilter, Relewise.Client",
          CategoryIds: options.collections,
          EvaluationScope: "ImmediateParent",
          TypeName: "ProductCategoryIdFilter",
          Negated: false,
        },
        {
          $type: "Relewise.Client.Requests.Filters.ProductIdFilter, Relewise.Client",
          ProductIds: [options.crossStoreProductId],
          TypeName: "ProductIdFilter",
          Negated: true,
        },
        {
          $type: "Relewise.Client.Requests.Filters.ProductDataFilter, Relewise.Client",
          Key: "Vendor",
          FilterOutIfKeyIsNotFound: true,
          MustMatchAllConditions: true,
          Conditions: {
            Items: [
              {
                $type: "Relewise.Client.Requests.Conditions.EqualsCondition, Relewise.Client",
                Value: {
                  Type: "String",
                  Value: options.vendor,
                },
                TypeName: "EqualsCondition",
                Negated: false,
              },
            ],
          },
          TypeName: "ProductDataFilter",
          Negated: false,
        },
      ],
    },
    DisplayedAtLocationType: "Fallback Popular Products",
  };

  return payload;
}

export function relewiseProductsSearch(key, location, vendor, max) {
  const settings = {
    language: window.mosmosh.shop_local.iso,
    currency: window.mosmosh.currency.iso,
    displayedAtLocation: location,
    user: getUserObject(),
  };

  const RELEWISE_BASE_API_KEY = STAGING ? urls.RELEWISE_STAGE_API_KEY : urls.RELEWISE_PROD_API_KEY;
  const RELEWISE_KEY = STAGING ? constants.RELEWISE_STAGE_KEY : constants.RELEWISE_PROD_KEY;

  let filter = (c) => {
    if (vendor === "Mos Mosh") {
      c.addEqualsCondition("Gallery", true);
    } else {
      c.addEqualsCondition(vendor);
    }
  };
  if (vendor === "all") {
    filter = null;
  }

  const request = new SearchCollectionBuilder()
    .addRequest(
      new Searcher(RELEWISE_BASE_API_KEY, RELEWISE_KEY).searchProducts(
        new ProductSearchBuilder(settings)
          .setSelectedProductProperties(requestDefaults.selectedProductProperties)
          .setSelectedVariantProperties(requestDefaults.selectedVariantProperties)
          .setTerm(key)
          .pagination((p) => p.setPageSize(max).setPage(1))
          .filters((f) =>
            f
              .addProductDataFilter("Vendor", (c) => (filter ? filter(c) : null))
              .addVariantAssortmentFilter([assortments[window.mosmosh.shop_code]])
              .addVariantDataFilter("ColorVariantId", (c) => c.addDistinctCondition(1), true, true)
          )
          .build()
      )
    )
    .addRequest(
      new Searcher(RELEWISE_BASE_API_KEY, RELEWISE_KEY).searchTermPrediction(
        new SearchTermPredictionBuilder(settings).addEntityType("Product").setTerm(key).take(5).build()
      )
    )
    .addRequest(
      new Searcher(RELEWISE_BASE_API_KEY, RELEWISE_KEY).searchProductCategories(
        new ProductCategorySearchBuilder(settings)
          .setSelectedCategoryProperties({
            displayName: true,
            dataKeys: ["Handle"],
          })
          .setTerm(key)
          .filters((f) =>
            f
              .addProductCategoryDataFilter("Vendor", (c) => (filter ? filter(c) : null))
              .addVariantAssortmentFilter([assortments[window.mosmosh.shop_code]])
          )
          .build()
      )
    );

  return { products: request.requests[0], predictions: request.requests[1], categories: request.requests[2] };
}

function postDataObject(endpoint, payload) {
  const RELEWISE_BASE_API = STAGING ? urls.RELEWISE_STAGE_API : urls.RELEWISE_PROD_API;
  const RELEWISE_KEY = STAGING ? constants.RELEWISE_STAGE_KEY : constants.RELEWISE_PROD_KEY;

  return fetch(RELEWISE_BASE_API + endpoint, {
    headers: {
      "Content-Type": "application/json",
      Authorization: "APIKey " + RELEWISE_KEY + "",
    },
    body: JSON.stringify(payload),
    history: false,
    method: "POST",
  }).catch((error) => {
    console.log(error);
  });
}
